// src/components/billing/UnlockPaywallButton.tsx
import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';
import SparkButton from '../common/SparkButton';

interface Props {
  eventId: string;
  label?: string;
  size?: 'small' | 'medium' | 'large';
  currentStep?: number; // Optional: kept for compatibility with callers in multi-step flows
}

const PUBLISH_PRICE_ID = 'price_1SBGyaRpbPCQ14ntYOzP4eIZ'; // your live price id

const UnlockPaywallButton: React.FC<Props> = ({
  eventId,
  label = 'Unlock & Send — $4.99',
  size = 'large',
  currentStep
}) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  /**
   * Unified return behavior:
   * - If checkout starts from any builder route (/create-event or /create-event-from-template),
   *   ALWAYS return to the Event Details page: /event/:eventId
   *   (both success and cancel map there, with ?payment=success|cancelled)
   *
   * - For non-builder contexts, preserve existing behavior and just append ?payment=
   *   to the current URL (and preserve ?step when provided).
   */
  const getReturnUrls = () => {
    const origin = window.location.origin;
    const currentPath = window.location.pathname;

    // Identify builder (multi-step) flows
    const isBuilderFlow =
      currentPath === '/create-event' ||
      currentPath === '/create-event-from-template';

    // NEW: Always land on Event Details when coming back from Stripe if we are in a builder flow
    if (isBuilderFlow && eventId) {
      const targetUrl = `/event/${eventId}`;
      const successUrl = `${origin}${targetUrl}?payment=success`;
      const cancelUrl = `${origin}${targetUrl}?payment=cancelled`;

      console.log('[UnlockPaywallButton] Success URL (builder):', successUrl);
      console.log('[UnlockPaywallButton] Cancel URL (builder):', cancelUrl);

      return { successUrl, cancelUrl };
    }

    // Fallback for non-builder contexts: keep existing behavior, preserve params
    const params = new URLSearchParams(window.location.search);

    // Preserve step if present in callers (back-compat)
    if (currentStep) {
      params.set('step', currentStep.toString());
    }

    // Create success URL
    const successParams = new URLSearchParams(params);
    successParams.set('payment', 'success');
    const successUrl = `${origin}${currentPath}?${successParams.toString()}`;

    // Create cancel URL
    const cancelParams = new URLSearchParams(params);
    cancelParams.set('payment', 'cancelled');
    const cancelUrl = `${origin}${currentPath}?${cancelParams.toString()}`;

    console.log('[UnlockPaywallButton] Success URL (fallback):', successUrl);
    console.log('[UnlockPaywallButton] Cancel URL (fallback):', cancelUrl);

    return { successUrl, cancelUrl };
  };

  const startCheckout = async () => {
    setBusy(true);
    setErr(null);
    try {
      const callable = httpsCallable(functions, 'createCheckoutSession');
      const { successUrl, cancelUrl } = getReturnUrls();

      const { data } = (await callable({
        eventId,
        priceId: PUBLISH_PRICE_ID,
        successUrl,
        cancelUrl,
      })) as any;

      if (data?.sessionId && data?.url) {
        window.location.href = data.url;
        return;
      }
      throw new Error('No checkout URL returned.');
    } catch (e: any) {
      console.error(e);
      if (e?.code === 'functions/resource-exhausted') {
        setErr('Please wait a few minutes before creating another checkout session.');
      } else if (e?.code === 'functions/failed-precondition') {
        setErr('Event distribution is already unlocked or there was a configuration error.');
      } else {
        setErr(e?.message || 'Could not start checkout. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ width: '100%' }}>
      <SparkButton onClick={startCheckout} disabled={busy} size={size}>
        {busy ? 'OPENING CHECKOUT…' : label}
      </SparkButton>
      {err && (
        <div
          style={{
            marginTop: 8,
            color: '#b00020',
            fontWeight: 600,
            fontSize: '0.875rem',
            textAlign: 'center',
          }}
        >
          {err}
        </div>
      )}
    </div>
  );
};

export default UnlockPaywallButton;
